import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHead, Kpi, StatusChip, PrioChip, Empty, errMsg } from "@/components/tm/TmParts";
import { OfficeTaskDetail } from "@/components/office/OfficeTaskDetail";

function TaskRows({ rows, onOpen, actions }) {
  if (!rows.length) return <Empty text="Nothing here." />;
  return (
    <div className="card-soft divide-y divide-border">
      {rows.map((t) => (
        <div key={t.task_id} className="flex flex-wrap items-center gap-2 p-3" data-testid={`appr-row-${t.task_id}`}>
          <div className="flex-1 min-w-[200px] cursor-pointer" onClick={() => onOpen(t.task_id)}>
            <p className="font-medium text-sm">{t.title}</p>
            <p className="text-xs text-muted-foreground">{t.task_id} · {t.assigned_to?.name} · {t.department}{t.completion_remarks ? ` · "${t.completion_remarks}"` : ""}</p>
          </div>
          <PrioChip p={t.priority} /><StatusChip s={t.status} />
          {actions && actions(t)}
        </div>))}
    </div>);
}

export default function Approvals({ tab = "approvals" }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [esc, setEsc] = useState([]);
  const [sub, setSub] = useState("pending");
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    setD((await api.get("/tm/approvals")).data);
    setEsc((await api.get("/office/escalations")).data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const act = async (tid, action, body) => {
    try { await api.post(`/office/tasks/${tid}/${action}`, body); toast.success(action === "verify" ? "Approved" : "Returned for rework"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resolve = async (eid) => {
    const note = window.prompt("Resolution note:"); if (note === null) return;
    try { await api.post(`/office/escalations/${eid}/resolve`, { note }); toast.success("Escalation resolved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  const openEsc = esc.filter((e) => e.status === "open");
  return (
    <div className="space-y-5" data-testid={`${tab}-page`}>
      <PageHead title={tab === "approvals" ? "Approval Centre" : "Escalation Centre"} sub={tab === "approvals" ? "Work submitted for review. Approve, or return with a recorded explanation." : "Overdue and manually escalated items by level. Resolve with a note."}
        right={<div className="flex rounded-md border border-border bg-card overflow-hidden">{[["approvals", "Approvals"], ["escalations", "Escalations"]].map(([k, l]) => <button key={k} onClick={() => nav(`/tm/${k}`)} className={`px-3 h-9 text-xs font-medium ${tab === k ? "bg-primary text-white" : ""}`} data-testid={`ae-tab-${k}`}>{l}</button>)}</div>} />
      {tab === "approvals" ? (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Pending my approval" value={d.pending.length} tone="text-purple-600" testid="appr-kpi-pending" /><Kpi label="Approved by me" value={d.approved.length} tone="text-emerald-600" />
          <Kpi label="Returned by me" value={d.returned.length} tone="text-signal-red" /><Kpi label="Waiting on others" value={d.waiting_on_others.length} />
        </div>
        <div className="flex gap-1">{[["pending", "Pending"], ["approved", "Approved"], ["returned", "Returned"], ["waiting_on_others", "Waiting"]].map(([k, l]) => <Button key={k} size="sm" variant={sub === k ? "default" : "outline"} className={sub === k ? "bg-primary text-white" : ""} onClick={() => setSub(k)} data-testid={`appr-sub-${k}`}>{l}</Button>)}</div>
        <TaskRows rows={d[sub]} onOpen={setOpen} actions={sub === "pending" ? (t) => (<>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(t.task_id, "verify", { remarks: null })} data-testid={`appr-approve-${t.task_id}`}>Approve</Button>
          <Button size="sm" variant="outline" className="text-signal-red" onClick={() => { const r = window.prompt("Reason for returning (required):"); if (r) act(t.task_id, "reject", { reason: r }); }} data-testid={`appr-return-${t.task_id}`}>Return</Button>
        </>) : null} />
      </>) : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Open" value={openEsc.length} tone="text-signal-red" testid="esc-kpi-open" />
          {[1, 2, 3].map((l) => <Kpi key={l} label={`Level ${l}`} value={openEsc.filter((e) => e.level === l).length} />)}
        </div>
        {esc.length === 0 ? <Empty text="No escalations." testid="esc-empty" /> : (
          <div className="card-soft divide-y divide-border">
            {esc.map((e) => (
              <div key={e.escalation_id} className="flex flex-wrap items-center gap-2 p-3" data-testid={`esc-row-${e.escalation_id}`}>
                <div className="flex-1 min-w-[200px]"><p className="font-medium text-sm">{e.title}</p><p className="text-xs text-muted-foreground">{e.escalation_id} · {e.reason} · raised by {e.raised_by} · {String(e.created_at).slice(0, 16).replace("T", " ")}</p></div>
                <span className={`tm-chip ${e.level >= 3 ? "tm-bad" : e.level === 2 ? "tm-warn" : "tm-progress"}`}>L{e.level}</span>
                <span className={`tm-chip ${e.status === "open" ? "tm-bad" : "tm-done"}`}>{e.status}</span>
                {e.office_task_id && <Button size="sm" variant="ghost" onClick={() => setOpen(e.office_task_id)}>Task</Button>}
                {e.status === "open" && <Button size="sm" variant="outline" onClick={() => resolve(e.escalation_id)} data-testid={`esc-resolve-${e.escalation_id}`}>Resolve</Button>}
              </div>))}
          </div>)}
      </>)}
      {open && <OfficeTaskDetail taskId={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>);
}
